/**
 * Aircraft class for purposes of identifying the type of aircraft a pattern altitude applies to
 */
export enum AircraftClass {
  Rotorcraft,
  HeavyAircraft,
  LightAircraft,
  TurbineAircraft
}


/**
 * Pattern altitude data for an airport
 */
export class PatternAltitude {
  /**
   * Pattern altitude ID that uniquely identifies this entry in the database.
   */
  patternAltId: number;

  /**
   * Airport ID where this pattern altitude is in effect.
   */
  airportId: number;

  /**
   * Source ID that identifies the source of this data (e.g., airport manager)
   */
  sourceId: number;

  /**
   * Pattern altitude value in feet MSL
   */
  altitude: number;

  /**
   * Aircraft class this pattern altitude applies to (e.g., Light Aircraft, Rotorcraft, etc.)
   */
  aircraftClass: AircraftClass;

  /**
   * Last update date for this entry
   */
  updateDate: Date;

  /**
   * User ID who last modified this entry
   */
  updateBy: string;

  /**
   * Parses pattern altitude data
   * @param data {any} Pattern altitude data as returned by API
   * @return {PatternAltitude} Parsed pattern altitude data
   */
  static parse(data: any): PatternAltitude {
    return {
      patternAltId: data.patternAltId,
      airportId: data.airportId,
      sourceId: data.sourceId,
      altitude: data.altitude,
      aircraftClass: this.parseAircraftClass(data.aircraftClass),
      updateDate: data.updateDate ? new Date(data.updateDate) : null,
      updateBy: data.updateBy
    };
  }

  /**
   * Parses aircraft class from string
   * @param value {string} Aircraft class as returned by API
   * @return {AircraftClass} Parsed aircraft class
   */
  static parseAircraftClass(value: string): AircraftClass {
    switch (value) {
      case 'Rotorcraft':
        return AircraftClass.Rotorcraft;
      case 'Heavy Aircraft':
        return AircraftClass.HeavyAircraft;
      case 'Light Aircraft':
        return AircraftClass.LightAircraft;
      case 'Turbine Aircraft':
        return AircraftClass.TurbineAircraft;
      default:
        return null;
    }
  }

  /**
   * Formats aircraft class for display
   * @param value {AircraftClass} Aircraft class to be formatted as string
   * @return {string} Formatted aircraft class
   */
  static formatAircraftClass(value: AircraftClass): string {
    switch (value) {
      case AircraftClass.Rotorcraft:
        return 'Rotorcraft';
      case AircraftClass.HeavyAircraft:
        return 'Heavy Aircraft';
      case AircraftClass.LightAircraft:
        return 'Light Aircraft';
      case AircraftClass.TurbineAircraft:
        return 'Turbine Aircraft';
      default:
        return 'All Aircraft';
    }
  }
}
